import { useEffect, useState } from "react";
import Link from "next/link";
import { useParams } from "react-router-dom";

import { apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";
import { ProductCard } from "@/components/product/product-card";

type StoreData = {
  vendor: {
    businessName: string;
    slug: string;
    description: string | null;
    logo: string | null;
    status: string;
    rating: number;
    reviewCount: number;
    vendorType: { name: string } | null;
    _count: { products: number };
  };
  products: { id: string; title: string; slug: string; price: number; images: string[]; rating: number }[];
};

export function StorePage() {
  const { slug } = useParams();
  const [data, setData] = useState<StoreData | null>(null);

  useEffect(() => {
    if (!slug) return;
    void apiGet<StoreData>(`/api/store/${slug}`).then((d) => setData(d));
  }, [slug]);

  if (!data) {
    return <div className="mx-auto max-w-amazon px-4 py-10 text-sm text-slate-500">Loading store…</div>;
  }

  const { vendor, products } = data;

  return (
    <div className="mx-auto max-w-amazon space-y-6 px-3 py-6 sm:px-4 sm:py-8">
      <div className="amazon-card flex flex-col items-start gap-4 p-4 sm:p-6 sm:flex-row sm:items-center">
        {vendor.logo ? (
          <img src={vendor.logo} alt={vendor.businessName} className="h-16 w-16 rounded-full object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amazon-navy text-xl font-black text-white">
            {vendor.businessName.slice(0, 2).toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="text-2xl font-black">{vendor.businessName}</h1>
          <p className="text-sm text-slate-500">
            {vendor.vendorType?.name ?? "Vendor"} · ⭐ {vendor.rating.toFixed(1)} ({vendor.reviewCount} reviews) ·{" "}
            {vendor._count.products} products
          </p>
          {vendor.description && <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">{vendor.description}</p>}
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-xl font-bold">Products</h2>
        {products.length === 0 ? (
          <p className="text-sm text-slate-500">No products listed yet.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 sm:gap-3">
            {products.map((p) => (
              <ProductCard
                key={p.id}
                product={{
                  id: p.id,
                  title: p.title,
                  slug: p.slug,
                  price: p.price,
                  mrp: p.price,
                  discount: 0,
                  images: p.images || [],
                  rating: p.rating || 4.5,
                  reviewCount: 0,
                  stock: 10,
                  brand: vendor.businessName,
                  category: "General",
                  description: "",
                  tags: [],
                  isPrime: false,
                  isFeatured: false
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
